<template>
  <v-container>
    <v-row>
      <v-col
        class="d-flex justify-md-center align-md-start justify-space-between"
        :cols="12"
        :sm="2"
      >
        <EventHighlightsIcon :highlights-count="highlightsCount" />
      </v-col>
      <template v-if="showEmptyContent">
        <v-col :cols="12" :sm="8">
          <EventHighlightsEmptyContent />
        </v-col>
      </template>
      <template v-else>
        <v-col :cols="12" :sm="5">
          <EventHighlightsFeaturedImage
            :image="featuredImage"
            :loading="isLoading"
            @click="openGalleryViewEvent.emit(true)"
          />
        </v-col>
        <v-col :cols="12" :sm="5">
          <EventHighlightsGrid
            :event="event"
            :highlights="highlights"
            :loading="isLoading"
            :open-gallery-view-event="openGalleryViewEvent"
            @reload="loadHighlights()"
          />
        </v-col>
      </template>
    </v-row>
    <div
      v-if="highlightAuthors.length > 0"
      style="display: flex; justify-content: center; margin-top: 16px"
    >
      <div
        v-html="highlightAuthorsText"
        style="font-size: 12px; font-style: italic"
      ></div>
    </div>
    <EventHighlightsView
      :open-event="openGalleryViewEvent"
      :event="event"
    ></EventHighlightsView>
  </v-container>
</template>

<script lang="ts">
import { computed, defineComponent, inject, PropType, provide } from "vue"
import { DetailedEvent } from "suus-api"
import EventHighlightsIcon from "features/events/highlights/EventHighlightsIcon.vue"
import EventHighlightsGrid from "features/events/highlights/highlights-grid/EventHighlightsGrid.vue"
import EventHighlightsFeaturedImage from "features/events/highlights/EventHighlightsFeaturedImage.vue"
import { GalleryService } from "features/gallery/service/gallery.service"
import { EventHighlightThumbnailService } from "features/events/highlights/event-highlight-thumbnail.service"
import { useHighlightsWithOptimism } from "features/events/highlights/concerns/use-highlights-with-optimism"
import EventHighlightsView from "features/events/highlights/EventHighlightsView.vue"
import { SimpleEventBus } from "core/event-bus/event-bus"
import EventHighlightsEmptyContent from "features/events/highlights/EventHighlightsEmptyContent.vue"
import Button from "components/UI/Button.vue"
import IconButton from "components/UI/IconButton.vue"
import { getContextName, ViewContext } from "concerns/useContextName"

export default defineComponent({
  name: "EventHighlightsTab",
  components: {
    IconButton,
    Button,
    EventHighlightsEmptyContent,
    EventHighlightsView,
    EventHighlightsFeaturedImage,
    EventHighlightsGrid,
    EventHighlightsIcon,
  },
  props: {
    event: {
      type: Object as PropType<DetailedEvent>,
      required: true,
    },
    viewContext: {
      type: Object as PropType<ViewContext>,
      required: true,
    },
  },
  setup(props) {
    const galleryService: GalleryService = inject("GalleryService")

    provide(
      "ThumbnailService",
      new EventHighlightThumbnailService(galleryService)
    )
    const highlightsCount = galleryService.highlightsCount

    const { highlights, isLoading, error, loadHighlights, featuredImage } =
      useHighlightsWithOptimism(props.event.id, galleryService)

    loadHighlights()

    const openGalleryViewEvent = new SimpleEventBus<boolean>()

    const showEmptyContent = computed(
      () => galleryService.highlightsCount.value === 0
    )
    const highlightAuthors = computed(() => {
      return props.event.contributors.map((contributor) =>
        getContextName(props.viewContext, contributor.group_member)
      )
    })
    const highlightAuthorsText = computed(() => {
      const boldHighlightAuthors = highlightAuthors.value.map(
        (author) => `<b>${author}</b>`
      )

      if (boldHighlightAuthors.length === 1)
        return `Dieses Highlight wurde von ${boldHighlightAuthors[0]} aufgenommen. Vielen Dank!`

      return `Diese Highlights wurden von ${boldHighlightAuthors
        .slice(0, boldHighlightAuthors.length - 1)
        .join(", ")} und ${
        boldHighlightAuthors[boldHighlightAuthors.length - 1]
      } aufgenommen. Vielen Dank!`
    })

    return {
      highlights,
      isLoading,
      error,
      loadHighlights,
      highlightsCount,
      featuredImage,
      openGalleryViewEvent,
      showEmptyContent,
      highlightAuthors,
      highlightAuthorsText,
    }
  },
})
</script>
